import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { addToCart as addToServerCart, updateCartItem, removeFromCart as removeFromServerCart, clearCart as clearServerCart, getCartItems } from '../services/cartService';
import api from '../config/api';

const CartContext = createContext();

export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // İlk yüklemede localStorage'dan veriyi al
    const savedCart = localStorage.getItem('guestCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [loading, setLoading] = useState(false);
  // İndirim kodu state'i ekle
  const [discount, setDiscount] = useState(() => {
    // İlk yüklemede localStorage'dan indirim bilgisini al
    const savedDiscount = localStorage.getItem('cartDiscount');
    return savedDiscount ? JSON.parse(savedDiscount) : null;
  });
  const { user } = useAuth();
  const eventListenerAdded = useRef(false);

  // İndirim değiştiğinde localStorage'ı güncelle
  useEffect(() => {
    if (discount) {
      localStorage.setItem('cartDiscount', JSON.stringify(discount));
    } else {
      localStorage.removeItem('cartDiscount');
    }
  }, [discount]);

  // Kullanıcı değiştiğinde cart verilerini güncelle
  useEffect(() => {
    const handleCartDataLoaded = (event) => {
      setCart(event.detail);
    };

    const handleGuestDataTransferred = async (event) => {
      const { cartTransferred, favoritesTransferred } = event.detail;
      
      if (user) {
        // Her zaman sepeti yeniden yükle (transfer olsun ya da olmasın)
        try {
          const serverCart = await getCartItems(user.id);
          const formattedCart = serverCart.map(item => ({
            id: Date.now() + Math.random(),
            product_id: item.product_id,
            product: item.product,
            selectedModel: item.phone_model,
            selectedColor: item.color,
            phone_model_id: item.phone_model_id,
            color_id: item.color_id,
            quantity: item.quantity,
            is_custom: item.product?.is_custom || false,
            price: item.product?.price || 0,
            isCustom_variable: item.product?.is_custom || false
          }));
          setCart(formattedCart);
        } catch (error) {
          // Sessizce hata yönetimi
        }
      }
    };

    const loadUserCart = async () => {
      if (user) {
        try {
          const serverCart = await getCartItems(user.id);
          // Sunucudan gelen verileri doğru formata dönüştür
          const formattedCart = serverCart.map(item => {
            const isCustom = item.product?.is_custom || false;
            
            const formattedItem = {
              ...item,
              selectedModel: item.phone_model ? {
                id: item.phone_model.id,
                brand: item.phone_model.brand,
                model_name: item.phone_model.model_name
              } : null,
              selectedColor: item.color ? {
                id: item.color.id,
                name: item.color.name,
                code: item.color.code
              } : null,
              // Custom design flag'ini ZORUNLU olarak set et (spread operator'dan sonra)
              is_custom: isCustom
            };
            
            
            return formattedItem;
          });
          setCart(formattedCart);
        } catch (error) {
          // Sessizce hata yönetimi
        }
      }
    };

    if (user) {
      loadUserCart();
      if (!eventListenerAdded.current) {
        window.addEventListener('cartDataLoaded', handleCartDataLoaded);
        window.addEventListener('guestDataTransferred', handleGuestDataTransferred);
        eventListenerAdded.current = true;
      }
    } else {
      // Kullanıcı çıkış yaptığında localStorage'dan yükle
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        setCart(JSON.parse(guestCart));
      } else {
        setCart([]);
      }
      // Event listener'ı temizle
      if (eventListenerAdded.current) {
        window.removeEventListener('cartDataLoaded', handleCartDataLoaded);
        window.removeEventListener('guestDataTransferred', handleGuestDataTransferred);
        eventListenerAdded.current = false;
      }
    }

    return () => {
      if (eventListenerAdded.current) {
        window.removeEventListener('cartDataLoaded', handleCartDataLoaded);
        window.removeEventListener('guestDataTransferred', handleGuestDataTransferred);
        eventListenerAdded.current = false;
      }
    };
  }, [user]);

  // Misafir kullanıcı için cart değiştiğinde localStorage'ı güncelle
  useEffect(() => {
    if (!user) {
      localStorage.setItem('guestCart', JSON.stringify(cart));
    }
  }, [cart, user]);

  const handleAddToCart = async (product, selectedModel, selectedColor, quantity = 1) => {
    try {
      if (user) {
        // Custom tasarım kontrolü
        const isCustomDesign = product.is_custom || false;
        
        // Stok kontrolü için API çağrısı - API yetersiz stok durumunda hata döndürecek
        const response = await addToServerCart(
          user.id, 
          product.id, 
          selectedModel?.id,
          selectedColor?.id,
          quantity
        );

        // API çağrısı başarılı olduysa sepeti güncelle
        const serverCart = await getCartItems(user.id);
        const formattedCart = serverCart.map(item => {
          const isCustom = item.product?.is_custom || false;
          console.log('🔍 AddToCart debug:', {
            product_id: item.product_id,
            product_name: item.product?.name,
            backend_is_custom: item.product?.is_custom,
            frontend_is_custom: isCustom
          });
          
          const formattedItem = {
            ...item,
            selectedModel: item.phone_model ? {
              id: item.phone_model.id,
              brand: item.phone_model.brand,
              model_name: item.phone_model.model_name
            } : null,
            selectedColor: item.color ? {
              id: item.color.id,
              name: item.color.name,
              code: item.color.code
            } : null,
            // Custom design flag'ini ZORUNLU olarak set et (spread operator'dan sonra)
            is_custom: isCustom
          };
          
          return formattedItem;
        });
        setCart(formattedCart);
        
        // API'den gelen stok bilgisini dönebiliriz
        return response;
      } else {
        // Misafir kullanıcı için yerel stok kontrolü yapmak gerekiyor
        // Özel tasarım değilse stok kontrolü yap
        const isCustomDesign = product.is_custom || product.is_custom_design || false;
        console.log('🔍 MISAFIR KULLANICI CUSTOM CHECK:', {
          product_is_custom: product.is_custom,
          product_is_custom_design: product.is_custom_design,
          final_isCustomDesign: isCustomDesign
        });
        
        if (!isCustomDesign) {
          try {
            // Ürün, model ve renk stok kontrolü için API çağrısı
            const stockResponse = await api.get(`/products/${product.id}/models/${selectedModel?.id}/colors/${selectedColor?.id}/stock`);
            const availableStock = stockResponse.data.stock || 0;
            
            // Sepetteki mevcut miktarı kontrol et
            const existingItemIndex = cart.findIndex(item =>
              item.product_id === product.id && 
              item.selectedModel?.id === selectedModel?.id && 
              item.selectedColor?.id === selectedColor?.id
            );
            
            if (existingItemIndex !== -1) {
              // Ürün zaten sepette - toplam miktar kontrolü
              const currentQuantity = cart[existingItemIndex].quantity;
              const totalQuantity = currentQuantity + quantity;
              
              // Stok kontrolü
              if (totalQuantity > availableStock) {
                throw {
                  response: {
                    data: {
                      error: 'Yetersiz stok',
                      available_stock: availableStock,
                      requested_quantity: quantity,
                      current_cart_quantity: currentQuantity
                    }
                  }
                };
              }
              
              // Stok yeterli, miktarı güncelle
              const updatedCart = [...cart];
              updatedCart[existingItemIndex].quantity = totalQuantity;
              setCart(updatedCart);
              localStorage.setItem('guestCart', JSON.stringify(updatedCart));
              
              return {
                message: 'Ürün miktarı güncellendi',
                available_stock: availableStock,
                cart_quantity: totalQuantity
              };
            } else {
              // Yeni ürün - miktar kontrolü
              if (quantity > availableStock) {
                throw {
                  response: {
                    data: {
                      error: 'Yetersiz stok',
                      available_stock: availableStock,
                      requested_quantity: quantity
                    }
                  }
                };
              }
              
              // Stok yeterli, yeni ürün ekle
              const newItem = {
                id: Date.now(),
                product_id: product.id,
                product: {
                  ...product,
                  images: product.images
                },
                selectedModel: selectedModel ? {
                  id: selectedModel.id,
                  brand: selectedModel.brand || '',
                  model_name: selectedModel.model_name || selectedModel.name || ''
                } : null,
                selectedColor: selectedColor ? {
                  id: selectedColor.id,
                  name: selectedColor.name,
                  code: selectedColor.code
                } : null,
                phone_model_id: selectedModel?.id,
                color_id: selectedColor?.id,
                quantity: quantity,
                is_custom: false,
                price: product.price
              };

              const updatedCart = [...cart, newItem];
              setCart(updatedCart);
              localStorage.setItem('guestCart', JSON.stringify(updatedCart));
              
              return {
                message: 'Ürün sepete eklendi',
                available_stock: availableStock,
                cart_quantity: quantity
              };
            }
          } catch (stockError) {
            // Stok sorgulama hatası
            console.error('Stok kontrolü hatası:', stockError);
            throw stockError;
          }
        } else {
          // Özel tasarım ürünleri için stok kontrolü
          try {
            // Özel tasarım için stok kontrolü API çağrısı
            const stockResponse = await api.get(`/phone-models/${selectedModel?.id}/colors/${selectedColor?.id || 0}/custom-design-stock?product_type_id=1`);
            const availableStock = stockResponse.data.stock || 0;
            
            const existingItemIndex = cart.findIndex(item =>
              item.product_id === product.id && 
              item.phone_model_id === selectedModel?.id
            );
            
            // Sepetteki diğer aynı model ürünlerin toplam miktarını kontrol et
            const cartItemsWithSameModel = cart.filter(item => 
              item.phone_model_id === selectedModel?.id && 
              item.is_custom === true
            );
            const totalQuantityInCart = cartItemsWithSameModel.reduce((sum, item) => sum + item.quantity, 0);
            
            // Stok kontrolü - sepetteki toplam miktarı da dahil et
            if (totalQuantityInCart + quantity > availableStock) {
              throw {
                response: {
                  data: {
                    error: 'Bu model için toplam sepet limiti aşıldı',
                    available_stock: availableStock,
                    current_cart_quantity: totalQuantityInCart,
                    requested_quantity: quantity,
                    custom_design: true
                  }
                }
              };
            }
            
            if (existingItemIndex !== -1) {
              // Ürün zaten sepette, toplam miktar için stok kontrolü
              const currentQty = cart[existingItemIndex].quantity;
              const totalQty = currentQty + quantity;
              
              // Stok kontrolü
              if (totalQty > availableStock) {
                throw {
                  response: {
                    data: {
                      error: 'Yetersiz stok',
                      available_stock: availableStock,
                      current_cart_quantity: currentQty,
                      requested_quantity: quantity,
                      custom_design: true
                    }
                  }
                };
              }
              
              // Stok yeterli, miktarı güncelle
              const updatedCart = [...cart];
              updatedCart[existingItemIndex].quantity = totalQty;
              setCart(updatedCart);
              localStorage.setItem('guestCart', JSON.stringify(updatedCart));
              
              return { 
                message: 'Özel tasarım ürün miktarı güncellendi',
                available_stock: availableStock,
                cart_quantity: totalQty
              };
            } else {
              // Yeni özel tasarım ürünü için stok kontrolü
              if (quantity > availableStock) {
                throw {
                  response: {
                    data: {
                      error: 'Yetersiz stok',
                      available_stock: availableStock,
                      requested_quantity: quantity,
                      custom_design: true
                    }
                  }
                };
              }
              
              // Stok yeterli, yeni ürün ekle
              const newItem = {
                id: Date.now(),
                product_id: product.id,
                product: {
                  ...product,
                  images: product.images || product.image,
                  is_custom: true  // Ürün objesinde de custom flag'i set et
                },
                selectedModel: selectedModel ? {
                  id: selectedModel.id,
                  brand: selectedModel.brand || '',
                  model_name: selectedModel.model_name || selectedModel.name || ''
                } : null,
                selectedColor: selectedColor ? {
                  id: selectedColor.id,
                  name: selectedColor.name,
                  code: selectedColor.code
                } : null,
                phone_model_id: selectedModel?.id,
                color_id: selectedColor?.id,
                quantity: quantity,
                is_custom: true,
                custom_design_image: product.images || product.image,
                price: product.price
              };

              const updatedCart = [...cart, newItem];
              setCart(updatedCart);
              localStorage.setItem('guestCart', JSON.stringify(updatedCart));
              
              return { 
                message: 'Özel tasarım ürün sepete eklendi',
                available_stock: availableStock,
                cart_quantity: quantity
              };
            }
          } catch (stockError) {
            console.error('Özel tasarım stok kontrolü hatası:', stockError);
            throw stockError;
          }
        }
      }
    } catch (error) {
      console.error('Sepete ekleme hatası:', error);
      throw error;
    }
  };

  const handleUpdateQuantity = async (productId, modelId, colorId, quantity) => {
    console.log('🎯 HANDLE UPDATE QUANTITY CALLED:', { productId, modelId, colorId, quantity, user: user?.id });
    try {
      if (user) {
        // API çağrısı yetersiz stok durumunda hata döndürecek
        await updateCartItem(user.id, productId, modelId, colorId, quantity);
        
        console.log('✅ UPDATE CART ITEM API SUCCESS - Fetching fresh cart...');
        
        // Başarılı güncelleme sonrası sepeti yenile
        const serverCart = await getCartItems(user.id);
        console.log('🗂️ FRESH SERVER CART:', serverCart);
        
        // Mevcut cart'ın sırasını korumak için mapping oluştur
        const currentCartOrder = cart.map((item, index) => ({
          product_id: item.product_id,
          phone_model_id: item.phone_model_id || item.selectedModel?.id,
          color_id: item.color_id || item.selectedColor?.id,
          order: index
        }));
        
        const formattedCart = serverCart.map(item => {
          const isCustom = item.product?.is_custom || false;
          console.log('🔄 UPDATE QUANTITY - Cart item debug:', {
            product_id: item.product_id,
            product_name: item.product?.name,
            backend_is_custom: item.product?.is_custom,
            frontend_is_custom: isCustom
          });
          
          const formattedItem = {
            ...item,
            selectedModel: item.phone_model ? {
              id: item.phone_model.id,
              brand: item.phone_model.brand,
              model_name: item.phone_model.model_name
            } : null,
            selectedColor: item.color ? {
              id: item.color.id,
              name: item.color.name,
              code: item.color.code
            } : null,
            // Custom design flag'ini ZORUNLU olarak set et (spread operator'dan sonra)
            is_custom: isCustom
          };
          
          console.log('✅ UPDATE QUANTITY - FINAL FORMATTED ITEM:', {
            product_id: formattedItem.product_id,
            final_is_custom: formattedItem.is_custom,
            isCustom_variable: isCustom
          });
          
          return formattedItem;
        });
        
        // Cart item'ları mevcut sıraya göre sırala
        const sortedCart = formattedCart.sort((a, b) => {
          const aOrder = currentCartOrder.find(order => 
            order.product_id === a.product_id && 
            order.phone_model_id === (a.phone_model_id || a.selectedModel?.id) &&
            order.color_id === (a.color_id || a.selectedColor?.id)
          )?.order ?? 999;
          
          const bOrder = currentCartOrder.find(order => 
            order.product_id === b.product_id && 
            order.phone_model_id === (b.phone_model_id || b.selectedModel?.id) &&
            order.color_id === (b.color_id || b.selectedColor?.id)
          )?.order ?? 999;
          
          return aOrder - bOrder;
        });
        
        setCart(sortedCart);
      } else {
        // Misafir kullanıcı için stok kontrolü
        // Önce eşleşen ürünü bul
        const itemIndex = cart.findIndex(item => {
          const itemProductId = Number(item.product_id);
          const itemModelId = item.is_custom ? Number(item.phone_model_id) : Number(item.selectedModel?.id);
          const itemColorId = Number(item.selectedColor?.id);
          const targetProductId = Number(productId);
          const targetModelId = Number(modelId);
          const targetColorId = Number(colorId);
          
          if (item.is_custom) {
            return itemProductId === targetProductId && itemModelId === targetModelId;
          } else {
            return itemProductId === targetProductId && itemModelId === targetModelId && itemColorId === targetColorId;
          }
        });
        
        if (itemIndex === -1) {
          throw new Error('Ürün sepette bulunamadı');
        }
        
        const item = cart[itemIndex];
        
        // Özel tasarım ürünleri için de stok kontrolü yap
        if (item.is_custom) {
          try {
            // Özel tasarım için stok kontrolü API çağrısı (kendin tasarla için özel endpoint)
            const stockResponse = await api.get(`/phone-models/${modelId}/colors/${colorId || 0}/custom-design-stock?product_type_id=1`);
            const availableStock = stockResponse.data.stock || 0;
            
            // Sepetteki diğer aynı model özel tasarım ürünlerin toplam miktarını kontrol et
            const cartItemsWithSameModel = cart.filter(item => 
              item.phone_model_id === Number(modelId) && 
              item.is_custom === true && 
              item.id !== cart[itemIndex].id
            );
            const totalQuantityInCart = cartItemsWithSameModel.reduce((sum, item) => sum + item.quantity, 0);
            
            // Stok kontrolü - sepetteki toplam miktarı da dahil et
            if (totalQuantityInCart + quantity > availableStock) {
              throw {
                response: {
                  data: {
                    error: 'Bu model için toplam sepet limiti aşıldı',
                    available_stock: availableStock,
                    current_cart_quantity: totalQuantityInCart,
                    requested_quantity: quantity,
                    custom_design: true
                  }
                }
              };
            }
            
            // Stok kontrolü
            if (quantity > availableStock) {
              throw {
                response: {
                  data: {
                    error: 'Yetersiz stok',
                    available_stock: availableStock,
                    requested_quantity: quantity,
                    custom_design: true
                  }
                }
              };
            }
            
            // Stok yeterli, sepeti güncelle
            const updatedCart = [...cart];
            updatedCart[itemIndex] = { ...item, quantity };
            setCart(updatedCart);
            localStorage.setItem('guestCart', JSON.stringify(updatedCart));
            return;
          } catch (stockError) {
            console.error('Özel tasarım stok kontrolü hatası:', stockError);
            throw stockError;
          }
        }
        
        // Normal ürünler için stok kontrolü yap
        try {
          // Ürün, model ve renk stok kontrolü için API çağrısı
          const stockResponse = await api.get(`/products/${productId}/models/${modelId}/colors/${colorId}/stock`);
          const availableStock = stockResponse.data.stock || 0;
          
          // Stok kontrolü
          if (quantity > availableStock) {
            throw {
              response: {
                data: {
                  error: 'Yetersiz stok',
                  available_stock: availableStock,
                  requested_quantity: quantity
                }
              }
            };
          }
          
          // Stok yeterli, sepeti güncelle
          const updatedCart = [...cart];
          updatedCart[itemIndex] = { ...item, quantity };
          setCart(updatedCart);
          localStorage.setItem('guestCart', JSON.stringify(updatedCart));
        } catch (stockError) {
          // Stok kontrolü hatası
          console.error('Stok kontrolü hatası:', stockError);
          throw stockError;
        }
      }
    } catch (error) {
      console.error('Miktar güncelleme hatası:', error);
      throw error;
    }
  };

  const handleRemoveFromCart = async (productId, modelId, colorId) => {
    try {
      if (user) {
        await removeFromServerCart(user.id, productId, modelId, colorId);
        const serverCart = await getCartItems(user.id);
        
        // Mevcut cart'ın sırasını korumak için mapping oluştur
        const currentCartOrder = cart.map((item, index) => ({
          product_id: item.product_id,
          phone_model_id: item.phone_model_id || item.selectedModel?.id,
          color_id: item.color_id || item.selectedColor?.id,
          order: index
        }));
        
        const formattedCart = serverCart.map(item => {
          const isCustom = item.product?.is_custom || false;
          return {
            ...item,
            selectedModel: item.phone_model ? {
              id: item.phone_model.id,
              brand: item.phone_model.brand,
              model_name: item.phone_model.model_name
            } : null,
            selectedColor: item.color ? {
              id: item.color.id,
              name: item.color.name,
              code: item.color.code
            } : null,
            is_custom: isCustom
          };
        });
        
        // Cart item'ları mevcut sıraya göre sırala
        const sortedCart = formattedCart.sort((a, b) => {
          const aOrder = currentCartOrder.find(order => 
            order.product_id === a.product_id && 
            order.phone_model_id === (a.phone_model_id || a.selectedModel?.id) &&
            order.color_id === (a.color_id || a.selectedColor?.id)
          )?.order ?? 999;
          
          const bOrder = currentCartOrder.find(order => 
            order.product_id === b.product_id && 
            order.phone_model_id === (b.phone_model_id || b.selectedModel?.id) &&
            order.color_id === (b.color_id || b.selectedColor?.id)
          )?.order ?? 999;
          
          return aOrder - bOrder;
        });
        
        setCart(sortedCart);
      } else {
        // Misafir kullanıcı için local sepetten kaldır
        const updatedCart = cart.filter(item => {
          // ID'leri number'a çevir
          const itemProductId = Number(item.product_id);
          const itemModelId = item.is_custom ? Number(item.phone_model_id) : Number(item.selectedModel?.id);
          const itemColorId = Number(item.selectedColor?.id);
          const targetProductId = Number(productId);
          const targetModelId = Number(modelId);
          const targetColorId = Number(colorId);

          // Özel tasarım kontrolü
          if (item.is_custom) {
            return !(itemProductId === targetProductId && itemModelId === targetModelId && itemColorId === targetColorId);
          } else {
            return !(itemProductId === targetProductId && itemModelId === targetModelId && itemColorId === targetColorId);
          }
        });

        setCart(updatedCart);
        localStorage.setItem('guestCart', JSON.stringify(updatedCart));
      }
    } catch (error) {
      console.error('Sepetten silme hatası:', error);
      throw error;
    }
  };

  const handleClearCart = useCallback(async () => {
    try {
      setLoading(true);
      if (user) {
        // Giriş yapmış kullanıcı için sunucudan sepeti temizle
        await clearServerCart(user.id);
        setCart([]);
      } else {
        // Misafir kullanıcı için local storage'ı temizle
        localStorage.removeItem('guestCart');
        setCart([]);
      }
      
      // İndirim bilgilerini de temizle
      setDiscount(null);
      localStorage.removeItem('cartDiscount');
      
      console.log('Sepet ve indirim bilgileri başarıyla temizlendi');
    } catch (error) {
      console.error('Sepet temizleme hatası:', error.response?.data || error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Misafir sepetini sunucuya aktarma fonksiyonu
  const transferGuestCartToUser = async () => {
    try {
      if (user && cart.length > 0) {
        setLoading(true);
        // Her ürünü sırayla sunucuya ekle
        for (const item of cart) {
          await addToServerCart(
            user.id, 
            item.product_id, 
            item.selectedModel?.id, 
            item.selectedColor?.id,
            item.quantity
          );
        }
        // Local sepeti temizle
        localStorage.removeItem('guestCart');
        // Sunucudan güncel sepeti al
        const serverCart = await getCartItems(user.id);
        // Sunucudan gelen verileri doğru formata dönüştür
        const formattedCart = serverCart.map(item => {
          const isCustom = item.product?.is_custom || false;
          return {
            ...item,
            selectedModel: item.phone_model ? {
              id: item.phone_model.id,
              brand: item.phone_model.brand,
              model_name: item.phone_model.model_name
            } : null,
            selectedColor: item.color ? {
              id: item.color.id,
              name: item.color.name,
              code: item.color.code
            } : null,
            is_custom: isCustom
          };
        });
        setCart(formattedCart);
      }
    } catch (error) {
      console.error('Sepet aktarma hatası:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // İndirim kodu uygulama fonksiyonu
  const applyDiscountCode = (code) => {
    // Boş kod kontrolü
    if (!code.trim()) {
      throw new Error('Lütfen bir indirim kodu girin.');
    }
    
    // İndirim kodlarını kontrol et
    const trimmedCode = code.trim().toUpperCase();
    
    if (trimmedCode === 'MRBROWN10') {
      setDiscount({
        code: 'MRBROWN10',
        percentage: 10,
        name: 'MRBROWN10 İndirimi'
      });
      return {
        code: 'MRBROWN10',
        percentage: 10,
        name: 'MRBROWN10 İndirimi'
      };
    } else {
      throw new Error('Geçersiz indirim kodu.');
    }
  };

  // İndirim kodu kaldırma fonksiyonu
  const removeDiscountCode = () => {
    setDiscount(null);
  };

  const value = {
    cart,
    loading,
    discount,
    addToCart: handleAddToCart,
    updateQuantity: handleUpdateQuantity,
    removeFromCart: handleRemoveFromCart,
    clearCart: handleClearCart,
    applyDiscountCode,
    removeDiscountCode,
    transferGuestCartToUser
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 